import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import pluralize from "pluralize";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion.tsx";
import {Card, CardDescription, CardHeader} from "@/components/ui/card.tsx";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMentorsWithMatchedMentees} from "@/services/mentorService.ts";
import MatchedMenteeCard from "@/components/MatchedMenteeCard.tsx";

const MentorsSuggestions = () => {
    const {t} = useTranslation();

    const {data: mentorsWithMatchedMentees, isFetching} = useQuery({
        queryKey: queryKeys.fetchSuggestions.mentors,
        queryFn: () => getMentorsWithMatchedMentees(),
        initialData: [],
    });

    if (isFetching) {
        return (
            <div className="flex flex-col space-y-3">
                <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                </div>
            </div>
        );
    }

    let hasMatchedMentees = false;
    const matchedMentees = mentorsWithMatchedMentees.map(mentor => {
        if (!hasMatchedMentees && mentor.suggestedTo && mentor.suggestedTo.length > 0) {
            hasMatchedMentees = true;
        }

        return (
            <AccordionItem key={mentor.id} value={mentor.id.toString()}>
                <AccordionTrigger className="flex justify-between items-center">
                    <div className="font-medium text-text-1">
                        {mentor.user.firstName} {mentor.user.lastName}
                    </div>
                    <div className="ml-auto mr-2 text-sm text-text-2">
                        {
                            mentor.suggestedTo.length === 0
                                ? mentor.careerFocusId
                                    ? <span className="text-tangerine">{t("suggestions.didNotMatchWithMentees")}</span>
                                    : <span>{t("suggestions.profileIncomplete")}</span>
                                : <span>Matched with {pluralize("mentee", mentor.suggestedTo?.length, true)}</span>
                        }
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <a
                        href={`/mentors/${mentor.id}`}
                        className="inline-flex items-center justify-center rounded-md border border-border bg-bg-card px-4 h-10 text-sm font-medium text-text-1 hover:bg-bg-elevated transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card"
                    >
                        {t("suggestions.goToMentorProfile")}
                    </a>
                    {mentor.suggestedTo && mentor.suggestedTo.length > 0 && (
                        <>
                            <h2 className="pt-6 text-lg font-semibold text-text-1">{t("suggestions.matchedMentees")}</h2>
                            <MatchedMenteeCard
                                suggestions={mentor.suggestedTo}
                                mentorAffinityGroupId={mentor.affinityGroupId ?? null}
                            />
                        </>
                    )}
                </AccordionContent>
            </AccordionItem>
        );
    });

    if (!hasMatchedMentees) {
        return (
            <Card className="bg-bg-card border border-border rounded-xl">
                <CardHeader>
                    <CardDescription className="text-base text-text-2">
                        {t("suggestions.notAvailableYet")}
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Accordion type="single" collapsible className="pt-0">
            {matchedMentees}
        </Accordion>
    );
}

export default MentorsSuggestions;
